import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric,
} from 'vue-router'
import routes from './routes'
import authority from '@/services/AuthService'
import { useUserStore } from '@/stores/useUserStore'
import { getStoryDetail } from '@/services/StoryService'
import type { AxiosError } from 'axios'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

const checkIsProtectedStory = async (
  to: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext,
): Promise<boolean> => {
  const storyId = to.params.itemId
  if (!storyId) {
    next({ name: 'Forbidden' })
    return false
  }

  try {
    const story = await getStoryDetail(storyId as string)
    if (!story || !story.acl.u) {
      next({ name: 'Forbidden' })
      return false
    }
  } catch (e) {
    if ((e as AxiosError).response?.status === 404) {
      return true
    }
  }

  return true
}

router.beforeEach(async (to, _, next) => {
  const userStore = useUserStore()
  if (authority.isActive) {
    await userStore.loadUser()
    await userStore.loadUserPreferences()
    if (to.meta.managerOnly && !userStore.isManager) {
      next({ name: 'Forbidden' })
      return
    }
    if (to.meta.isProtectedStory && !(await checkIsProtectedStory(to, next))) return

    next()
    return
  }

  if ((to.meta.requiresAuth || to.meta.isProtectedStory || to.meta.managerOnly) && !authority.isActive) {
    window.location.assign(
      import.meta.env.VITE_APP_BACK_URL +
        '/account/login/?next=' +
        encodeURIComponent(
          import.meta.env.VITE_APP_FRONT_URL + router.resolve({ name: 'Handshake', query: { log: to.fullPath } }).href,
        ),
    )
    return
  }

  next()
})

export default router
